import {
    Code2,
    Terminal,
    Database,
    Globe,
    Smartphone,
    CircuitBoard,
    Layers,
    Brain,
    Monitor,
    Zap,
    Cloud,
    Shield,
    Cpu,
    GitBranch
} from "lucide-react";

const technologies = [
    { name: 'React', icon: Code2, category: 'Frontend', level: 'Expert' },
    { name: 'Node.js', icon: Terminal, category: 'Backend', level: 'Expert' },
    { name: 'Python', icon: Database, category: 'Backend', level: 'Advanced' },
    { name: 'TypeScript', icon: CircuitBoard, category: 'Language', level: 'Expert' },
    { name: 'React Native', icon: Smartphone, category: 'Mobile', level: 'Expert' },
    { name: 'Next.js', icon: Monitor, category: 'Framework', level: 'Expert' },
    { name: 'PostgreSQL', icon: Database, category: 'Database', level: 'Advanced' },
    { name: 'AWS', icon: Cloud, category: 'Cloud', level: 'Advanced' },
    { name: 'Docker', icon: Layers, category: 'DevOps', level: 'Advanced' },
    { name: 'GraphQL', icon: Zap, category: 'API', level: 'Expert' },
    { name: 'AI/ML', icon: Brain, category: 'Intelligence', level: 'Intermediate' },
    { name: 'Kubernetes', icon: Layers, category: 'DevOps', level: 'Intermediate' }
];

const techStacks = [
    {
        title: "Frontend Moderno",
        icon: Globe,
        description: "Interfaces reativas e performáticas que encantam usuários",
        techs: ["React", "Next.js", "TypeScript", "Tailwind"],
        gradient: "from-blue-500/10 to-cyan-500/10",
        borderColor: "border-blue-500/20"
    },
    {
        title: "Backend Robusto",
        icon: Terminal,
        description: "APIs escaláveis e seguras para suportar qualquer demanda",
        techs: ["Node.js", "Python", "PostgreSQL", "Redis"],
        gradient: "from-green-500/10 to-emerald-500/10",
        borderColor: "border-green-500/20"
    },
    {
        title: "Cloud & DevOps",
        icon: Cloud,
        description: "Infraestrutura moderna para máxima disponibilidade",
        techs: ["AWS", "Docker", "Kubernetes", "CI/CD"],
        gradient: "from-purple-500/10 to-violet-500/10",
        borderColor: "border-purple-500/20"
    },
    {
        title: "Mobile Nativo",
        icon: Smartphone,
        description: "Apps nativos para iOS e Android com performance superior",
        techs: ["React Native", "Flutter", "Swift", "Kotlin"],
        gradient: "from-orange-500/10 to-red-500/10",
        borderColor: "border-orange-500/20"
    }
];

const TechnologiesSection = () => {
    return (
        <section className="relative py-6 overflow-hidden bg-gradient-to-b from-background via-muted/5 to-background">
            {/* Background Pattern */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-primary/10 rounded-full filter blur-3xl opacity-30"></div>
                <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-accent/10 rounded-full filter blur-3xl opacity-30"></div>
                <div className="absolute top-10 right-20 w-24 h-24 text-primary/5">
                    <Code2 className="w-full h-full" />
                </div>
                <div className="absolute bottom-10 left-16 w-20 h-20 text-accent/5">
                    <Database className="w-full h-full" />
                </div>
            </div>

            <div className="container-page relative z-10">
                {/* Header */}
                <div className="text-center mb-16 space-y-6">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full text-sm font-medium text-primary">
                        <Cpu className="w-4 h-4" />
                        <span>Stack Tecnológico Avançado</span>
                    </div>

                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight">
                        <span className="block">Dominamos as</span>
                        <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                            Tecnologias Mais Modernas
                        </span>
                    </h2>

                    <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-3xl mx-auto">
                        Utilizamos as <strong className="text-primary">melhores ferramentas do mercado</strong> para criar
                        soluções <strong className="text-primary">robustas, escaláveis e seguras</strong> que impulsionam seu negócio.
                    </p>
                </div>

                {/* Tech Stacks Overview */}
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
                    {techStacks.map((stack, index) => (
                        <div
                            key={index}
                            className={`relative p-6 rounded-2xl border ${stack.borderColor} bg-gradient-to-br ${stack.gradient} hover:shadow-xl hover:scale-105 transition-all duration-300 group overflow-hidden`}
                        >
                            <div className="relative z-10 space-y-4">
                                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                                    <stack.icon className="w-6 h-6 text-primary" />
                                </div>

                                <div className="space-y-2">
                                    <h3 className="text-lg font-bold text-foreground">{stack.title}</h3>
                                    <p className="text-sm text-muted-foreground leading-relaxed">{stack.description}</p>
                                </div>

                                <div className="flex flex-wrap gap-1">
                                    {stack.techs.map((tech, idx) => (
                                        <span key={idx} className="text-xs bg-primary/10 px-2 py-1 rounded-full text-foreground font-medium">
                                            {tech}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Technologies Grid */}
                <div className="mb-16">
                    <h3 className="text-3xl font-bold text-center mb-8">
                        <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                            Tecnologias & Ferramentas
                        </span>
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6">
                        {technologies.map((tech) => (
                            <div
                                key={tech.name}
                                className="group cursor-pointer"
                            >
                                <div className="relative flex flex-col items-center p-4 rounded-xl bg-card border hover:border-primary/40 hover:shadow-lg hover:scale-105 transition-all duration-300 overflow-hidden">
                                    {/* Level indicator */}
                                    <div className={`absolute top-2 right-2 w-2 h-2 rounded-full ${tech.level === 'Expert' ? 'bg-green-500' :
                                        tech.level === 'Advanced' ? 'bg-blue-500' : 'bg-yellow-500'
                                        }`}></div>

                                    {/* Icon */}
                                    <div className="w-12 h-12 mb-3 relative">
                                        <div className="w-full h-full bg-gradient-to-br from-primary/10 to-accent/10 rounded-xl flex items-center justify-center group-hover:from-primary/20 group-hover:to-accent/20 transition-all duration-300">
                                            <tech.icon className="w-6 h-6 text-primary group-hover:scale-110 transition-transform duration-300" />
                                        </div>
                                    </div>

                                    {/* Tech Info */}
                                    <div className="text-center space-y-1">
                                        <span className="text-sm font-semibold text-foreground block">
                                            {tech.name}
                                        </span>
                                        <span className="text-xs text-muted-foreground">
                                            {tech.category}
                                        </span>
                                    </div>

                                    {/* Hover indicator */}
                                    <div className="w-0 h-0.5 bg-gradient-to-r from-primary to-accent rounded-full group-hover:w-8 transition-all duration-300 mt-2"></div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Bottom CTA Section */}
                <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-primary/10 via-background to-accent/10 border p-8 md:p-12">
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-primary/10 rounded-full filter blur-3xl opacity-50"></div>

                    <div className="relative z-10 text-center space-y-6">
                        <div className="space-y-4">
                            <h3 className="text-2xl md:text-3xl font-bold text-foreground">
                                Pronto para
                                <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent"> revolucionar </span>
                                seu projeto?
                            </h3>

                            <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                                Nossa expertise em tecnologias modernas garante que seu projeto seja desenvolvido
                                com as melhores práticas e ferramentas do mercado.
                            </p>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-6 justify-center items-center pt-4">
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <Shield className="w-4 h-4 text-primary" />
                                <span>Código limpo e documentado</span>
                            </div>
                            <div className="hidden sm:block w-px h-4 bg-border"></div>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <GitBranch className="w-4 h-4 text-primary" />
                                <span>Versionamento profissional</span>
                            </div>
                            <div className="hidden sm:block w-px h-4 bg-border"></div>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <Zap className="w-4 h-4 text-primary" />
                                <span>Performance otimizada</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default TechnologiesSection;